import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * InviteUsersCard — Phase C of Workspace Link Sharing.
 *
 * Renders inside Admin → Organization. Lets admins:
 *   1) Send a workspace invite by email. The recipient gets a branded
 *      Kids WiseApp email with a one-click "Accept invitation" CTA that
 *      lands them on /invite/:token, pre-filled with the org's branding.
 *   2) See pending / accepted / revoked / expired invites for their org.
 *   3) Resend, revoke, copy URL, or delete records.
 *
 * Security UX notes:
 *   - Admin-tier roles (org_admin / org_owner / deputy_manager) only show
 *     up in the role picker for org_owner / super_admin callers; deputies
 *     and regular admins see staff/parent/driver/room_leader only. The
 *     backend enforces the same rule (403 otherwise).
 *   - Once an invite is "accepted" it's read-only.
 */

const STATUS_BADGES = {
  pending: { label: 'Pending', bg: 'hsl(45 95% 92%)', fg: 'hsl(35 80% 30%)' },
  accepted: { label: 'Accepted', bg: 'hsl(140 70% 92%)', fg: 'hsl(140 55% 26%)' },
  revoked: { label: 'Revoked', bg: 'hsl(0 75% 94%)', fg: 'hsl(0 60% 35%)' },
  expired: { label: 'Expired', bg: 'hsl(220 15% 92%)', fg: 'hsl(220 15% 35%)' },
};

const ROLE_LABEL = {
  parent: 'Parent',
  staff: 'Staff',
  driver: 'Driver',
  room_leader: 'Room Leader',
  deputy_manager: 'Deputy Manager',
  org_admin: 'Organization Admin',
  org_owner: 'Organization Owner',
};

function formatDate(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

export function InviteUsersCard({ organization, currentUser }) {
  const callerRole = currentUser?.role || '';
  const canInviteAdminTier = ['org_owner', 'super_admin'].includes(callerRole);

  const centers = useMemo(
    () => (organization?.centers || []).filter((c) => c.is_active !== false),
    [organization]
  );
  const multiCenter = centers.length > 1;

  const [form, setForm] = useState({
    email: '',
    role: 'staff',
    center_id: centers[0]?.center_id || '',
    personal_message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const [invites, setInvites] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  // Keep default center synced if the org changes after mount.
  useEffect(() => {
    setForm((f) => ({
      ...f,
      center_id: f.center_id || centers[0]?.center_id || '',
    }));
  }, [centers]);

  const refreshInvites = useCallback(async () => {
    setLoadingList(true);
    try {
      const rows = await api.listWorkspaceInvites();
      setInvites(Array.isArray(rows) ? rows : []);
    } catch (err) {
      toast.error(err.message || 'Failed to load invites');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    refreshInvites();
  }, [refreshInvites]);

  const filteredInvites = useMemo(() => {
    if (statusFilter === 'all') return invites;
    return invites.filter((i) => i.status === statusFilter);
  }, [invites, statusFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const email = (form.email || '').trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      const created = await api.createWorkspaceInvite({
        email,
        role: form.role,
        center_id: multiCenter ? (form.center_id || undefined) : undefined,
        personal_message: form.personal_message?.trim() || undefined,
      });
      const dispatch = created?.email_dispatch || {};
      if (dispatch.ok || dispatch.status === 'sent') {
        toast.success('Invite sent', { description: `Email delivered to ${email}.` });
      } else {
        toast.warning('Invite created — email could not be delivered', {
          description: dispatch.error || 'Share the link manually using the Copy URL button.',
          duration: 6000,
        });
      }
      setForm((f) => ({ ...f, email: '', personal_message: '' }));
      await refreshInvites();
    } catch (err) {
      toast.error(err.message || 'Failed to send invite');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (invite) => {
    try {
      const r = await api.resendWorkspaceInvite(invite.id);
      const dispatch = r?.email_dispatch || {};
      if (dispatch.ok || dispatch.status === 'sent') {
        toast.success(`Invite re-sent to ${invite.email}`);
      } else {
        toast.warning('Resent — but email could not be delivered', {
          description: dispatch.error || 'Share the link manually.',
        });
      }
      await refreshInvites();
    } catch (err) {
      toast.error(err.message || 'Failed to resend invite');
    }
  };

  const handleRevoke = async (invite) => {
    if (!window.confirm(`Revoke invite for ${invite.email}? The link will stop working.`)) return;
    try {
      await api.revokeWorkspaceInvite(invite.id);
      toast.success('Invite revoked');
      await refreshInvites();
    } catch (err) {
      toast.error(err.message || 'Failed to revoke');
    }
  };

  const handleDelete = async (invite) => {
    if (!window.confirm(`Delete invite record for ${invite.email}? This cannot be undone.`)) return;
    try {
      await api.deleteWorkspaceInvite(invite.id);
      toast.success('Invite deleted');
      await refreshInvites();
    } catch (err) {
      toast.error(err.message || 'Failed to delete');
    }
  };

  const handleCopyUrl = async (invite) => {
    const url = invite.invite_url
      || (typeof window !== 'undefined' && invite.token ? `${window.location.origin}/invite/${invite.token}` : '');
    if (!url) {
      toast.error('No invite URL available for this record.');
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Invite link copied to clipboard');
    } catch {
      // Fallback for restricted clipboard contexts
      window.prompt('Copy this invite URL:', url);
    }
  };

  const roleOptions = useMemo(() => {
    const base = [
      { value: 'parent', label: 'Parent' },
      { value: 'staff', label: 'Staff' },
      { value: 'driver', label: 'Driver' },
      { value: 'room_leader', label: 'Room Leader' },
    ];
    if (canInviteAdminTier) {
      base.push(
        { value: 'deputy_manager', label: 'Deputy Manager (admin-tier)' },
        { value: 'org_admin', label: 'Organization Admin' },
      );
    }
    return base;
  }, [canInviteAdminTier]);

  return (
    <div
      id="invite-users-card"
      className="card"
      data-testid="invite-users-card"
      style={{ marginBottom: '1.5rem' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: '0 0 0.25rem 0' }}>✉️ Invite users to your workspace</h3>
          <p style={{ margin: 0, fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
            Send a branded email with a one-click join link. Invites expire after 7 days.
          </p>
        </div>
      </div>

      {/* Invite form */}
      <form
        onSubmit={handleSubmit}
        data-testid="invite-users-form"
        style={{
          marginTop: '1rem',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '0.75rem',
          alignItems: 'end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
          <span>Email address *</span>
          <input
            type="email"
            required
            autoComplete="off"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="name@example.com"
            data-testid="invite-email-input"
            style={inputStyle}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
          <span>Role *</span>
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            data-testid="invite-role-select"
            style={inputStyle}
          >
            {roleOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>

        {multiCenter && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
            <span>Center</span>
            <select
              value={form.center_id || ''}
              onChange={(e) => setForm({ ...form, center_id: e.target.value })}
              data-testid="invite-center-select"
              style={inputStyle}
            >
              {centers.map((c) => (
                <option key={c.center_id} value={c.center_id}>{c.name || c.center_id}</option>
              ))}
            </select>
          </label>
        )}

        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem', gridColumn: '1 / -1' }}>
          <span>Personal message (optional)</span>
          <textarea
            rows={2}
            maxLength={400}
            value={form.personal_message}
            onChange={(e) => setForm({ ...form, personal_message: e.target.value })}
            placeholder="Add a short note that appears in the invite email"
            data-testid="invite-message-input"
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </label>

        <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
            data-testid="invite-submit-btn"
          >
            {submitting ? 'Sending…' : 'Send invitation'}
          </button>
        </div>
      </form>

      {/* List header */}
      <div
        style={{
          marginTop: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0.5rem',
        }}
      >
        <h4 style={{ margin: 0, fontSize: '0.95rem' }}>Invitations</h4>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="invite-status-filter"
            style={{ ...inputStyle, padding: '0.4rem 0.5rem', fontSize: '0.85rem' }}
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="revoked">Revoked</option>
            <option value="expired">Expired</option>
          </select>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={refreshInvites}
            data-testid="invite-refresh-btn"
            style={{ padding: '0.4rem 0.7rem', fontSize: '0.85rem' }}
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* List */}
      <div style={{ marginTop: '0.75rem', overflowX: 'auto' }}>
        {loadingList ? (
          <div style={{ padding: '1rem', color: 'hsl(var(--muted-foreground))' }}>Loading invitations…</div>
        ) : filteredInvites.length === 0 ? (
          <div
            data-testid="invite-empty-state"
            style={{
              padding: '1.25rem',
              border: '1px dashed hsl(var(--border))',
              borderRadius: 'var(--radius)',
              color: 'hsl(var(--muted-foreground))',
              textAlign: 'center',
              fontSize: '0.9rem',
            }}
          >
            No invitations match this filter yet. Send your first one above ✨
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }} data-testid="invite-list-table">
            <thead>
              <tr style={{ textAlign: 'left', color: 'hsl(var(--muted-foreground))', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Role</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Sent</th>
                <th style={thStyle}>Expires</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvites.map((inv) => {
                const badge = STATUS_BADGES[inv.status] || STATUS_BADGES.pending;
                const isTerminal = ['accepted', 'revoked', 'expired'].includes(inv.status);
                return (
                  <tr key={inv.id} data-testid={`invite-row-${inv.id}`} style={{ borderTop: '1px solid hsl(var(--border))' }}>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 500 }}>{inv.email}</div>
                      {inv.personal_message && (
                        <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: 2 }}>
                          “{inv.personal_message}”
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>{ROLE_LABEL[inv.role] || inv.role}</td>
                    <td style={tdStyle}>
                      <span
                        data-testid={`invite-status-${inv.id}`}
                        style={{
                          background: badge.bg,
                          color: badge.fg,
                          padding: '0.15rem 0.55rem',
                          borderRadius: '999px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          letterSpacing: 0.2,
                        }}
                      >
                        {badge.label}
                      </span>
                    </td>
                    <td style={tdStyle}>{formatDate(inv.last_sent_at || inv.created_at)}</td>
                    <td style={tdStyle}>{formatDate(inv.expires_at)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '0.35rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {inv.invite_url && (
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => handleCopyUrl(inv)}
                            data-testid={`invite-copy-${inv.id}`}
                            style={miniBtn}
                            title="Copy invite URL"
                          >
                            📋 Copy URL
                          </button>
                        )}
                        {!isTerminal && (
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => handleResend(inv)}
                            data-testid={`invite-resend-${inv.id}`}
                            style={miniBtn}
                          >
                            ↻ Resend
                          </button>
                        )}
                        {!isTerminal && (
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => handleRevoke(inv)}
                            data-testid={`invite-revoke-${inv.id}`}
                            style={{ ...miniBtn, color: 'hsl(0 60% 40%)' }}
                          >
                            🚫 Revoke
                          </button>
                        )}
                        {inv.status !== 'accepted' && (
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => handleDelete(inv)}
                            data-testid={`invite-delete-${inv.id}`}
                            style={{ ...miniBtn, color: 'hsl(var(--muted-foreground))' }}
                            title="Delete record"
                          >
                            🗑
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  padding: '0.55rem 0.7rem',
  border: '1px solid hsl(var(--border))',
  borderRadius: 'var(--radius)',
  background: '#fff',
  fontSize: '0.92rem',
};

const thStyle = {
  padding: '0.5rem 0.6rem',
  fontWeight: 600,
};

const tdStyle = {
  padding: '0.6rem',
  verticalAlign: 'top',
};

const miniBtn = {
  padding: '0.3rem 0.55rem',
  fontSize: '0.78rem',
  borderRadius: 'var(--radius)',
};

export default InviteUsersCard;
